import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import CloseButtonIcon from '~/components/Icons/svgs/CloseButtonIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { formatDateForCurriculumCertificate } from '~/shared/sanity/utils/curriculum.utils'
import useCurriculumGradeLabels from '~/curriculum/hooks/useCurriculumGradeLabels'

import { TopicId } from '~/dashboard/utils/learningPath.utils'

import { CurriculumCertificate } from '../types/curriculum.types'

interface Props {
  data: CurriculumCertificate | null
  showCertificateModal: () => void
  onClose: () => void
}

export default function AnimatedImageSwitcher({ data, showCertificateModal, onClose }: Props): React.ReactElement {
  const { t } = useTranslation('curriculum')
  const [stage, setStage] = useState(1)
  const topicId = data?.topicId ?? ''
  const gradeGroup = useMemo(() => (data ? [data] : []), [data])
  const gradeLabelOf = useCurriculumGradeLabels(gradeGroup)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const certificateUrl = `curriculum/certificate/mastery-certificate-${topicId}_v4.png`

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(2), isSafari ? 3320 : 3500), // Show second image after 3320 mili seconds
      setTimeout(() => setStage(3), isSafari ? 5320 : 5500), // Show final div after another 2 seconds
    ]

    return () => timers.forEach(clearTimeout)
  }, [isSafari])

  const animationMediaWithDetection = isSafari ? (
    <img
      alt="Certificate Animation"
      className="inset-0 w-195 max-w-full h-auto  transition-opacity duration-500"
      src={`https://assets.houseofmath.com/curriculum/certificate-animation-${topicId}${
        topicId === TopicId.ALGEBRA ? '_v2' : ''
      }.gif`}
    />
  ) : (
    <video autoPlay className="inset-0 w-195 max-w-full h-auto  transition-opacity duration-500" loop playsInline>
      <source
        src={`https://assets.houseofmath.com/curriculum/certificate-animation-${topicId}.webm`}
        type="video/webm"
      />
    </video>
  )

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 99999 }}>
      <button className="absolute z-10 top-16 right-1 md:right-2" onClick={onClose}>
        <SVGIcon icon={CloseButtonIcon} pathClassName="text-white" size={24} />
      </button>

      <div className="w-195 max-w-full h-144 max-h-full rounded flex justify-center items-center ">
        {stage === 1 && animationMediaWithDetection}

        {stage === 2 && (
          <img
            alt="Clapping Hands Animation"
            className="inset-0 w-150 max-w-full h-auto transition-opacity duration-500"
            src="https://images.houseofmath.com/icons/emojis/clapping-hands-animated.png"
          />
        )}
        {stage === 3 && (
          <div className="relative bg-purple-darker w-full h-full rounded flex items-center justify-center">
            <ResponsiveImage
              className="w-150 h-110 -mt-8 -ml-8 object-cover"
              height={800}
              srcKey="curriculum/certificate/big-ray.png"
              width={800}
            />
            <div className="absolute z-10 top-5 w-full h-full flex flex-col text-center text-white my-2 px-2">
              <p className="text-3xl md:text-6xl font-bold "> {t('mastered')}</p>
              <p className="text-2xl md:text-3xl py-2 md:py-4 font-bold">
                {data?.curriculumSubject} for {gradeLabelOf(data)}
              </p>
              <p className="font-bold">{formatDateForCurriculumCertificate(data?.date ?? '')}</p>
              <ResponsiveImage
                className=""
                height={300}
                imageClassName="mx-auto w-60 md:mt-5"
                srcKey={certificateUrl}
                width={300}
              />
              <button
                className="flex mx-auto text-center justify-center items-center border w-80 h-13 bg-purple mt-4 rounded"
                onClick={showCertificateModal}
              >
                {t('clickToViewCertificate')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
